import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, FormControl, InputLabel, Select, MenuItem,
  Alert, CircularProgress
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { Session } from '../../types/sessions';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';

interface CopySectionsDialogProps {
  open: boolean;
  targetSessionId: string;
  onClose: () => void;
  onCopied: () => void;
}

const CopySectionsDialog: React.FC<CopySectionsDialogProps> = ({
  open, targetSessionId, onClose, onCopied
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sourceId, setSourceId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const all = await sessionsApi.list({ includeArchived: true });
        if (cancelled) return;
        // Only sessions other than the target are valid sources.
        setSessions(all.filter(s => s._id !== targetSessionId));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, targetSessionId]);

  const submit = async () => {
    if (!sourceId) return;
    setSubmitting(true);
    try {
      const result = await sectionInstancesApi.bulkCopyFromSession({
        sourceSessionId: sourceId,
        targetSessionId
      });
      const created = result?.created ?? 0;
      const skipped = result?.skipped ?? 0;
      enqueueSnackbar(
        created > 0
          ? `${created} section${created === 1 ? '' : 's'} copied${skipped ? ` · ${skipped} already existed` : ''}`
          : 'Nothing to copy — sections already match',
        { variant: created > 0 ? 'success' : 'info' }
      );
      onCopied();
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not copy sections', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <ContentCopy color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={600}>Copy sections from another session</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Section names, rooms, and capacity are copied across. Existing sections in this session
          aren't touched.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={24} /></Stack>
        ) : sessions.length === 0 ? (
          <Alert severity="info" variant="outlined">No other sessions yet to copy from.</Alert>
        ) : (
          <FormControl fullWidth>
            <InputLabel>Source session</InputLabel>
            <Select
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              label="Source session"
            >
              {sessions.map(s => (
                <MenuItem key={s._id} value={s._id}>
                  {s.name}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    · {format(new Date(s.startDate), 'MMM yyyy')}–{format(new Date(s.endDate), 'MMM yyyy')}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={submit}
          variant="contained"
          disableElevation
          disabled={!sourceId || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? 'Copying…' : 'Copy sections'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopySectionsDialog;
